import { progressStateRepository, vacationAllowanceRepository } from '../db/database';
import {
  SINGLETON_ID,
  VACATION_STREAK_CAP,
  VACATION_STREAK_MILESTONE,
  type IsoDate,
  type VacationAllowance,
} from '../models/types';

/**
 * Tatil hakkı — CLAUDE.md §7.7.
 *
 * Kesintisiz 90 günlük Day Streak, kullanıcıya "tatil günü" hakkı
 * kazandırır. Tatil günü ASİMETRİKTİR: o gün ceza işlemez ama kazanç
 * normal işler ve gün otomatik olarak streak'e sayılır.
 *
 * Cezanın atlanması ve streak'in otomatik sayılması `scoring.ts`
 * içinde zaten uygulanıyor (`isFrozenDay` bayrağı üzerinden); bu
 * dosya hakkın AÇILMASINI ve KULLANILMASINI yönetir.
 */

/** Hiç hak kaydı yokken kullanılan boş başlangıç. */
function createEmptyAllowance(): VacationAllowance {
  return {
    id: SINGLETON_ID,
    totalDaysGranted: 0,
    daysUsed: 0,
    unlockedAt: null,
    grants: [],
    usedDates: [],
  };
}

/** Kullanılmamış (kalan) hak sayısı. */
export function getRemainingDays(allowance: VacationAllowance | undefined): number {
  if (!allowance) return 0;
  return allowance.totalDaysGranted - allowance.daysUsed;
}

/** Belirli bir gün tatil günü olarak işaretlenmiş mi? */
export function isVacationDate(
  allowance: VacationAllowance | undefined,
  date: IsoDate,
): boolean {
  return allowance?.usedDates.includes(date) ?? false;
}

/* ==================================================================
   Hakkın açılması (90 günlük kilometre taşı)
   ================================================================== */

/**
 * Streak bir 90 katına ulaştıysa tatil hakkını 10 güne TAMAMLAR.
 *
 * §7.7'deki tavan kuralı:
 * - Kalan hak 10'un altındaysa havuz 10 güne çıkarılır (fark eklenir).
 * - Kalan hak 10 veya üzerindeyse hiçbir ekleme yapılmaz.
 * Tavan yalnızca Day Streak'ten gelen bu eklemeye uygulanır; havuz
 * başka kaynaklarla 10'u aşabilir ve bu kural onu kırpmaz.
 *
 * Streak'in nasıl ilerlediğine BAKILMAZ: doğal tamamlanma, tatil günü
 * ya da Ödüller'den satın alınan gün — hepsi aynı sayacı ilerletir,
 * bu yüzden burada tek bir sayıya bakmak yeterli (§7.7).
 *
 * Not: aynı kilometre taşının iki kez ödüllendirilmesine karşı ayrı
 * bir koruma koymuyoruz. Streak bir değere yalnızca bir kez çıkar;
 * seri kopup tekrar 90'a ulaşırsa bu YENİ bir kilometre taşıdır ve
 * hak yeniden açılmalıdır — geçmiş ödülleri kontrol eden bir koruma,
 * tam da bu meşru durumu engellerdi.
 *
 * Bir şey değiştiyse güncel kaydı, değişmediyse `undefined` döndürür.
 */
export async function grantVacationOnMilestone(
  currentStreak: number,
  today: IsoDate,
): Promise<VacationAllowance | undefined> {
  // 0 da 90'a tam bölünür; seri sıfırlandığında ödül verilmemeli.
  if (currentStreak === 0 || currentStreak % VACATION_STREAK_MILESTONE !== 0) {
    return undefined;
  }

  const existing = await vacationAllowanceRepository.get();
  const allowance = existing ?? createEmptyAllowance();
  const remaining = getRemainingDays(allowance);

  // Zaten 10 veya üzeri hak duruyorsa ekleme yapılmaz.
  if (remaining >= VACATION_STREAK_CAP) {
    return undefined;
  }

  const daysToAdd = VACATION_STREAK_CAP - remaining;

  const updated: VacationAllowance = {
    ...allowance,
    totalDaysGranted: allowance.totalDaysGranted + daysToAdd,
    // İlk açılış tarihi bir kez yazılır, sonraki tamamlamalar bunu
    // değiştirmez.
    unlockedAt: allowance.unlockedAt ?? today,
    grants: [
      ...allowance.grants,
      {
        id: crypto.randomUUID(),
        days: daysToAdd,
        source: 'dayStreak',
        grantedAt: today,
        streakMilestone: currentStreak,
      },
    ],
  };

  await vacationAllowanceRepository.upsert(updated);
  return updated;
}

/* ==================================================================
   Hakkın kullanılması
   ================================================================== */

export type UseVacationResult =
  | { ok: true; remainingDays: number }
  | { ok: false; reason: 'noAllowance' | 'alreadyUsedToday' | 'alreadyCovered' };

/**
 * Bugünü tatil günü olarak işaretler.
 *
 * Yalnızca BUGÜN için çalışır: geçmiş günler `dailyScoreLedger`'a
 * kapanmış ve §7.2 gereği değiştirilemez durumdadır, geleceğe dönük
 * işaretleme ise §7.7'de tanımlı değil.
 *
 * Kaydın etkisi gün kapanışında ortaya çıkar (ceza atlanır), ama
 * streak'e otomatik sayılma etkisi ANINDA görünür — Streak Detay
 * ekranındaki hero ve bugünün takvim hücresi hemen değişir.
 */
export async function useVacationDay(today: IsoDate): Promise<UseVacationResult> {
  const [allowance, progress] = await Promise.all([
    vacationAllowanceRepository.get(),
    progressStateRepository.get(),
  ]);

  if (!allowance || getRemainingDays(allowance) <= 0) {
    return { ok: false, reason: 'noAllowance' };
  }
  // Aynı günü iki kez işaretlemek hakkı boşa harcardı.
  if (isVacationDate(allowance, today)) {
    return { ok: false, reason: 'alreadyUsedToday' };
  }
  // §7.6 — O gün ödülle satın alınmışsa gün zaten dondurulmuş; üstüne
  // bir de tatil hakkı harcamak karşılıksız bir kayıp olurdu.
  if (progress && progress.rewardDates.includes(today)) {
    return { ok: false, reason: 'alreadyCovered' };
  }

  const updated: VacationAllowance = {
    ...allowance,
    daysUsed: allowance.daysUsed + 1,
    usedDates: [...allowance.usedDates, today].sort(),
  };

  await vacationAllowanceRepository.upsert(updated);
  return { ok: true, remainingDays: getRemainingDays(updated) };
}
